import Konva from 'konva';
import * as state from '../state.js';
import { attachInteractive, pointerPos } from './_helpers.js';

// Blur tool. Four modes:
//   pixelate    — Konva.Filters.Pixelate at intensity
//   blur-secure — heavier Konva.Filters.Blur
//   blur-smooth — lighter Konva.Filters.Blur
//   blackout    — solid black (custom filter)
//
// Each blur is a Konva.Group containing a single Konva.Image. The image's
// crop matches the user's selection 1:1 in source pixels, and the image
// itself fills the group's bounds exactly — no negative offsets, no extra
// padded source data. Konva's StackBlur and Pixelate both clamp at the
// image edges (Blur.js samples `widthMinus1`; Pixelate.js skips out-of-
// bounds pixels), so the kernel never reads transparent pixels and there
// is no edge fringe to compensate for. Earlier revisions tried to pad the
// cached source by ~kernel-radius pixels with the inner image positioned
// at (-pad, -pad); that interacted badly with Konva's cache() auto-rect
// (which is derived from getSelfRect and ignores the node's own x/y),
// leaving an unblurred ring of the underlying screenshot visible inside
// the user's selection on the right and bottom edges.

let active = null;
let outline = null;
let mode = 'pixelate';
let intensity = 10;
let startX = 0;
let startY = 0;
let pendingRaf = 0;

function BlackoutFilter(imageData) {
  const d = imageData.data;
  for (let i = 0; i < d.length; i += 4) {
    d[i] = 0;
    d[i + 1] = 0;
    d[i + 2] = 0;
  }
}

function setFilter(imageNode, modeKey, level) {
  if (modeKey === 'pixelate') {
    imageNode.filters([Konva.Filters.Pixelate]);
    imageNode.pixelSize(Math.max(2, Math.round(level)));
  } else if (modeKey === 'blur-secure') {
    imageNode.filters([Konva.Filters.Blur]);
    imageNode.blurRadius(Math.max(8, level * 1.5));
  } else if (modeKey === 'blur-smooth') {
    imageNode.filters([Konva.Filters.Blur]);
    imageNode.blurRadius(Math.max(2, level * 0.6));
  } else if (modeKey === 'blackout') {
    imageNode.filters([BlackoutFilter]);
  }
  imageNode._blurMode = modeKey;
  imageNode._blurIntensity = level;
}

function applyGeometry(engine, node, x, y, w, h) {
  if (!(node instanceof Konva.Group)) {
    node.setAttrs({ x, y, width: w, height: h });
    return;
  }
  node.setAttrs({ x, y, width: w, height: h });
  const inner = node._inner;
  if (!inner) return;
  const layout = engine.layout;
  if (!layout.dw || !layout.dh) return;
  const sx = layout.naturalW / layout.dw;
  const sy = layout.naturalH / layout.dh;
  inner.setAttrs({
    x: 0,
    y: 0,
    width: w,
    height: h,
    crop: {
      x: (x - layout.ox) * sx,
      y: (y - layout.oy) * sy,
      width: w * sx,
      height: h * sy,
    },
  });
}

function makeNode(engine, x, y, w, h) {
  if (!engine._screenshotImg || !engine.layout.dw || !engine.layout.dh) {
    // Defensive fallback — engine not ready, dim the area instead of blurring.
    return new Konva.Rect({ x, y, width: w, height: h, fill: 'rgba(0,0,0,0.4)' });
  }
  const group = new Konva.Group({ x, y, width: w, height: h });
  const inner = new Konva.Image({ image: engine._screenshotImg });
  // Default hitFunc on Konva.Image samples opaque pixels; that's flaky for
  // a filtered image (StackBlur can leave near-transparent corners). Force
  // the hit area to the full inner rect so the user can grab the blur
  // anywhere inside it.
  inner.hitFunc(function (ctx, shape) {
    ctx.beginPath();
    ctx.rect(0, 0, shape.width(), shape.height());
    ctx.closePath();
    ctx.fillStrokeShape(shape);
  });
  group.add(inner);
  group._inner = inner;
  group._engine = engine;
  setFilter(inner, mode, intensity);
  applyGeometry(engine, group, x, y, w, h);
  return group;
}

function cacheActive() {
  if (!active) return;
  if (active instanceof Konva.Group && active._inner) {
    active._inner.cache();
  } else if (active instanceof Konva.Image) {
    active.cache();
  }
}

function bindRecrop(engine, node) {
  if (!(node instanceof Konva.Group) || !node._inner) return;
  let raf = 0;

  // Live tick: re-source the inner image's crop to match the node's
  // currently-visible bounds (position × scale). Crucially, we do NOT
  // mutate node.width / scaleX / scaleY here — Konva's Transformer is
  // mid-gesture and reads `width × scaleX` to track the active drag.
  // Writing width back during the gesture compounds (next tick reads the
  // already-grown width and multiplies by scale again), so we let the
  // Group keep its live scale and only refresh what the user is actually
  // looking at: the source pixels under the box. inner.width stays at
  // the pre-gesture size; the Group's scale stretches the cache visually
  // to the right size. Slightly softer rendering mid-gesture, snaps clean
  // on release.
  const liveUpdateCrop = () => {
    raf = 0;
    const inner = node._inner;
    if (!inner) return;
    const layout = engine.layout;
    if (!layout.dw || !layout.dh) return;
    const sx = layout.naturalW / layout.dw;
    const sy = layout.naturalH / layout.dh;
    const visW = node.width() * (node.scaleX() || 1);
    const visH = node.height() * (node.scaleY() || 1);
    inner.crop({
      x: (node.x() - layout.ox) * sx,
      y: (node.y() - layout.oy) * sy,
      width: visW * sx,
      height: visH * sy,
    });
    inner.cache();
    engine.bgEffectsLayer.batchDraw();
  };

  // End of gesture: bake the live scale into width/height so the node
  // returns to scaleX/Y === 1, then re-apply geometry cleanly. `bakeScale`
  // in _helpers.js explicitly skips Groups, so without this the Group
  // keeps scale > 1 forever and the next gesture compounds against it.
  const bakeAndApply = () => {
    raf = 0;
    const sx = node.scaleX() || 1;
    const sy = node.scaleY() || 1;
    const w = node.width() * sx;
    const h = node.height() * sy;
    applyGeometry(engine, node, node.x(), node.y(), w, h);
    if (sx !== 1 || sy !== 1) {
      node.scaleX(1);
      node.scaleY(1);
    }
    node._inner.cache();
    engine.bgEffectsLayer.batchDraw();
  };

  const scheduleLive = () => {
    if (raf) return;
    raf = requestAnimationFrame(liveUpdateCrop);
  };

  node.on('dragmove.recrop transform.recrop', scheduleLive);
  node.on('dragend.recrop transformend.recrop', bakeAndApply);
}

function scheduleCache(engine) {
  if (pendingRaf || !active) return;
  pendingRaf = requestAnimationFrame(() => {
    pendingRaf = 0;
    if (!active) return;
    cacheActive();
    engine.bgEffectsLayer.batchDraw();
  });
}

export const blur = {
  id: 'blur',
  defaultProps: { mode: 'pixelate', intensity: 10 },
  onPointerDown(engine) {
    const pos = pointerPos(engine);
    if (!engine.pointerInsideImage(pos)) return;
    startX = pos.x;
    startY = pos.y;
    mode = state.get('toolDefaults').blur?.mode ?? 'pixelate';
    intensity = state.get('toolDefaults').blur?.intensity ?? 10;
    // Start at 1×1 so the filter cache has a non-zero buffer to render into.
    active = makeNode(engine, startX, startY, 1, 1);
    // bgEffectsLayer sits between the screenshot and the highlight scrim, so
    // the blur gets dimmed like any other piece of the screenshot when the
    // user hasn't highlighted that region. Without this, blur regions look
    // like a second highlight on top of an otherwise-dimmed page.
    engine.bgEffectsLayer.add(active);
    cacheActive();
    // Marquee outline so the user can see the exact bounds of what's being
    // blurred — drawn on uiLayer (above content) so the cached filter doesn't
    // bake the stroke into the saved annotation.
    outline = new Konva.Rect({
      x: startX, y: startY, width: 1, height: 1,
      stroke: '#155EEF',
      strokeWidth: 2,
      strokeScaleEnabled: false,
      listening: false,
    });
    engine.uiLayer.add(outline);
    engine.bgEffectsLayer.batchDraw();
    engine.uiLayer.batchDraw();
  },
  onPointerMove(engine) {
    if (!active) return;
    const pos = pointerPos(engine);
    const dx = pos.x - startX;
    const dy = pos.y - startY;
    const x = dx < 0 ? pos.x : startX;
    const y = dy < 0 ? pos.y : startY;
    const w = Math.max(1, Math.abs(dx));
    const h = Math.max(1, Math.abs(dy));
    applyGeometry(engine, active, x, y, w, h);
    outline?.setAttrs({ x, y, width: w, height: h });
    engine.uiLayer.batchDraw();
    if (active instanceof Konva.Group) {
      scheduleCache(engine);
    } else {
      engine.bgEffectsLayer.batchDraw();
    }
  },
  onPointerUp(engine) {
    if (pendingRaf) {
      cancelAnimationFrame(pendingRaf);
      pendingRaf = 0;
    }
    outline?.destroy();
    outline = null;
    engine.uiLayer.batchDraw();
    if (!active) return;
    const w = active.width();
    const h = active.height();
    if (w < 4 || h < 4) {
      active.destroy();
      active = null;
      engine.bgEffectsLayer.batchDraw();
      return;
    }
    cacheActive();
    active.remove();
    attachInteractive(engine, active, { parent: engine.bgEffectsLayer });
    bindRecrop(engine, active);
    engine.bgEffectsLayer.batchDraw();
    active = null;
  },
  // Live property updates from the toolbar — the engine routes mode /
  // intensity changes here when an existing blur is selected. Swap the
  // filter and re-cache so the new filter actually runs against the
  // existing source crop.
  apply(node, prop, value) {
    if (!(node instanceof Konva.Group) || !node._inner) return;
    const engine = node._engine;
    if (!engine) return;
    const inner = node._inner;
    const currentMode = inner._blurMode ?? 'pixelate';
    const currentIntensity = inner._blurIntensity ?? 10;
    if (prop === 'mode') {
      setFilter(inner, value, currentIntensity);
    } else if (prop === 'intensity') {
      setFilter(inner, currentMode, value);
    } else {
      return;
    }
    inner.cache();
  },
};
